/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import {
    object, shape, array, string, bool, number, func, arrayOf,
} from 'prop-types';
import { compose } from 'recompose';
import { useSelector } from 'react-redux';

import { makeStyles } from '@material-ui/styles';
import { getSSRDeviceType } from '../../../../state/ducks/App/App-Selectors';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import withPageView from '../../../helpers/tracking/hocs/withPageView';
import { trackPage, coreBrowsingPageViewSuccessTest } from '../../../helpers/tracking/common/commonTrackingHelpers';
import { getFeatureFlags, getPresentationFamily } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import GraphqlSearchProducts from './Partials/GraphqlSearchProducts';
import GraphqlSearchErrorPage from './Partials/GraphqlSearchErrorPage';
import GraphqlAfterProductBanner from '../GraphqlCategoryPage/Partials/GraphqlAfterProductBanner/GraphqlAfterProductBanner';
import GraphqlSearchCollections from './Partials/GraphqlSearchCollections';
import GraphqlSearchArticles from './Partials/GraphqlSearchArticles';
import GraphqlSearchFacetFilter from './Partials/GraphqlSearchFacetFilter';
import SelectedFilters from '../GraphqlCategoryPage/Partials/SelectedFilters/SelectedFilters';

const useStyles = makeStyles((theme) => ({
    siteContainer: {
        margin: '0 15px',
        maxWidth: '1400px',
        [theme.breakpoints.up(1430)]: {
            margin: '0 auto',
        },
        [theme.breakpoints.down(336)]: {
            margin: '0 7px',
        },
    },
    header: {
        WebkitFontSmoothing: 'antialiased',
        textAlign: 'left',
        width: '100%',
    },
    numOfResultsContainer: {
        display: 'flex',
        alignItems: 'center',
        lineHeight: '1.2',
        padding: '20px 0',
        width: '100%',
        position: 'relative',
    },
    resultsText: {
        color: theme.palette.colorNeutral70,
        fontFamily: 'Roboto, sans-serif',
        fontSize: '20px',
        letterSpacing: '.03em',
        textShadow: '1px 1px 1px rgba(0, 0, 0, 0.004)',
        padding: '0 10px',
        textOverflow: 'ellipsis',
        overflowX: 'hidden',
        '& a': {
            color: theme.palette.primary.main,
            textDecoration: 'none',
        },
        [theme.breakpoints.down(600)]: {
            paddingLeft: '10px',
        },
    },
    resultsTextMain: {
        display: 'block',
        width: '100%',
        textAlign: 'center',
        '& h1': {
            margin: '5px 0',
            fontSize: 36,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
        },
        [theme.breakpoints.down(600)]: {
            '& h1': {
                fontSize: 30,
            },
        },
    },
    productBanner: {
        marginTop: '10px',
    },
    displayFlexContainer: {
        width: '100%',
    },
    filterMobile: {
        margin: ' 0 auto',
        marginTop: 15,
    },
    showingResultsFor: {
        fontSize: '18px',
        marginBottom: '5px',
        [theme.breakpoints.up('md')]: {
            fontSize: '25px',
        },
        '&>span': {
            fontWeight: 700,
        },
    },
    originalQuery: {
        fontSize: '14px',
        [theme.breakpoints.up('md')]: {
            fontSize: '16px',
        },
    },
    leftMenu: {
        width: '100%',
        position: 'relative',
        [theme.breakpoints.up(599)]: {
            width: '31.5%',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: '99',
        },
        [theme.breakpoints.up(769)]: {
            width: '260px',
            position: 'relative',
        },
        [theme.breakpoints.up(1025)]: {
            zIndex: '9',
        },
        [theme.breakpoints.down(1025)]: {
            display: 'none',
        },
    },
    productContainer: {
        width: '100%',
        marginLeft: 0,
        [theme.breakpoints.up(1024)]: {
            width: 'calc(100% - 290px)',
            marginLeft: '30px',
        },
    },
    productContainerWithOutFacet: {
        width: '100%',
        marginLeft: 0,
    },
}));

function GraphqlSearchPageContainer({
    displayDeliveryMethod,
    fetchMoreResults,
    filterOptions,
    loadMoreCount,
    pageCount,
    pageData,
    searchResults,
    searchTerm,
    setFilterOptions,
    track,
    productFacetsGroup,
    collectionData,
    articleData,
    variables,
    history,
}) {
    const brand = useSelector(getBrand);
    const presentationFamily = useSelector(getPresentationFamily);
    const device = useSelector(getSSRDeviceType);
    const featureFlags = useSelector(getFeatureFlags);

    const [filterData, setFilterData] = useState({});
    const classes = useStyles();

    const setFilterDataInCategoryBuilder = (data, isFacet = false) => {
        const filterDataList = filterOptions;
        if (isFacet) {
            filterDataList.facets = data;
        } else {
            filterDataList.facets = [];
        }
        setFilterOptions(filterDataList);
        setFilterData(data);
    };

    const {
        products, totalProducts, totalPages, showingResultsFor, originalQuery, experiments = [],
    } = pageData;
    const facetFilterEnabled = featureFlags['is-search-facet-filter-enabled'];
    let productsPerRow = facetFilterEnabled ? 3 : 4; // leave as default just in case the rest fails
    const productLayout = searchResults?.product_layout || [];
    let j;
    let bannerBlock;
    let bannerPositionTop;
    const categoryBlocks = searchResults?.category_blocks || [];
    categoryBlocks.forEach((block) => {
        if (block.banner) {
            bannerBlock = block.banner;
            bannerPositionTop = bannerBlock?.position === 'Top';
        }
    });
    // loop thru cms data
    for (j = 0; j < productLayout.length; j += 1) {
        if (productLayout[j]?.name?.includes('Desktop')) {
            productsPerRow = productLayout[j]?.products_per_row;
        }
    }
    // sort filter hide on search page based on feature flag
    const isFilterEnabled = featureFlags['is-sort-on-search-temp'];
    const isSearchDidYouMean = featureFlags['search-did-you-mean'];

    return (
        <div className={classes.siteContainer}>
            {(bannerBlock && bannerPositionTop) && (
                <Grid className={classes.productBanner}>
                    <GraphqlAfterProductBanner bannerBlock={bannerBlock} trackEvent={track} device={device} />
                </Grid>
            )}
            <Grid className={classes.header}>
                <Grid className={classes.numOfResultsContainer}>
                    {totalProducts > 0 && showingResultsFor && isSearchDidYouMean
                        ? (
                            <Grid container className={classes.resultsText}>
                                <Grid item xs={12} className={classes.showingResultsFor}> Showing results for  <span>{showingResultsFor}</span></Grid>
                                <Grid item xs={12} className={classes.originalQuery}> Show results for <Link to={`${originalQuery}?exact=1`}>{originalQuery}</Link> instead</Grid>
                            </Grid>
                        )
                        : <Grid className={classes.resultsText}>{`${(totalProducts > 0) ? totalProducts : products.length} ${totalProducts === 1 ? 'Result' : 'Results'} for "${decodeURIComponent(searchTerm)}"`}</Grid>}
                </Grid>
                {facetFilterEnabled && productFacetsGroup.length > 0 && <GraphqlSearchFacetFilter productFacetsGroup={productFacetsGroup} setFilterData={setFilterDataInCategoryBuilder} />}
                <Grid container>
                    <Grid item className={facetFilterEnabled && productFacetsGroup.length > 0 ? classes.leftMenu : ''}>
                        {facetFilterEnabled && productFacetsGroup.length > 0 && <GraphqlSearchFacetFilter desktop productFacetsGroup={productFacetsGroup} setFilterData={setFilterDataInCategoryBuilder} />}
                    </Grid>
                    <Grid item className={facetFilterEnabled && productFacetsGroup.length > 0 ? classes.productContainer : classes.productContainerWithOutFacet} id="productContainer">
                        {featureFlags['is-facets-v1-chips-enabled'] && facetFilterEnabled && (
                            <SelectedFilters
                                productFacetsBase={productFacetsGroup}
                                setFacetsDataInCollection={setFilterDataInCategoryBuilder}
                                facetsData={filterOptions?.facets || []}
                                history={history}
                            />
                        )}
                        {products !== null && products?.length > 0 ? (
                            <GraphqlSearchProducts
                                brand={brand}
                                device={device}
                                totalProducts={totalProducts}
                                fetchMoreResults={fetchMoreResults}
                                loadMoreCount={loadMoreCount}
                                pageNumber={pageCount}
                                products={products}
                                eventSubType="search"
                                productsPerRow={productsPerRow}
                                searchTerm={searchTerm}
                                totalPages={totalPages}
                                variables={variables}
                                priceRangeData={productFacetsGroup?.find((ele) => ele?.name === 'Price Range')}
                                experiments={experiments}
                            />
                        ) : (
                            <GraphqlSearchErrorPage
                                classes={classes}
                                products={products}
                                isFilterEnabled={isFilterEnabled}
                                presentationFamily={presentationFamily}
                                filterData={filterData}
                                brand={brand}
                                pageNumber={pageCount}
                                displayDeliveryMethod={displayDeliveryMethod}
                                setFilterDataInCategoryBuilder={setFilterDataInCategoryBuilder}
                                searchTerm={searchTerm}
                            />
                        )}
                    </Grid>
                </Grid>
                {collectionData?.collections?.length ? (
                    <div>
                        <GraphqlSearchCollections
                            collectionData={collectionData}
                        />
                    </div>
                ) : null}
                {(bannerBlock && !bannerPositionTop) && (
                    <Grid className={classes.productBanner}>
                        <GraphqlAfterProductBanner bannerBlock={bannerBlock} trackEvent={track} device={device} />
                    </Grid>
                )}
                {articleData?.blogs?.length ? (
                    <div>
                        <GraphqlSearchArticles
                            articleData={articleData}
                            searchTerm={searchTerm}
                        />
                    </div>
                ) : null}
            </Grid>
        </div>
    ); // if returned an array with products
}

GraphqlSearchPageContainer.propTypes = {
    searchTerm: string,
    pageData: shape({
        products: array.isRequired,
        redirectUrl: string,
        showingResultsFor: string,
        originalQuery: string,
        experiments: array,
    }).isRequired,
    displayDeliveryMethod: bool,
    loadMoreCount: number,
    pageCount: number.isRequired,
    setFilterOptions: func.isRequired,
    fetchMoreResults: func.isRequired,
    track: func.isRequired,
    searchResults: shape({
        category_blocks: arrayOf(
            shape({
                image: shape({
                    link: shape({
                        href: string.isRequired,
                        title: string.isRequired,
                    }),
                    mobile_image: shape({
                        url: string.isRequired,
                    }),
                }),
            }),
        ),
    }),
    filterOptions: object,
    productFacetsGroup: arrayOf(shape({
        displayOpen: bool,
        name: string,
    })),
    collectionData: shape({
        collections: arrayOf(shape({
            brand: string,
            name: string,
            description: string,
            redirectUrl: string,
            image: string,
            seo: object,
        })),
        total: number,
        pages: number,
    }),
    articleData: shape({
        blogs: arrayOf(shape({
            uid: string,
            title: string,
            excerpt: string,
            image: string,
            contentType: string,
            dateModified: string,
            datePublished: string,
            url: string,
        })),
        total: number,
        pages: number,
    }),
    variables: object,
    history: object.isRequired,
};
GraphqlSearchPageContainer.defaultProps = {
    searchTerm: '',
    displayDeliveryMethod: true,
    searchResults: {},
    loadMoreCount: 1,
    filterOptions: {},
    productFacetsGroup: [],
    collectionData: {},
    articleData: {},
    variables: {},
};

const enhance = compose(
    withRouter,
    trackPage({
        type: 'search',
        name: 'Search',
    }),
    withPageView({
        pageViewSuccessTest: coreBrowsingPageViewSuccessTest,
        updateEveryRender: true,
    }),
);

export default enhance(GraphqlSearchPageContainer);
